/**
 * S3 download filenames look like:
 *   image__{section}_{row}_{seat}_{timestamp}.png
 * Tokens must not contain underscores — the operator Single Image Per Seat
 * parser splits on `_`.
 */

export type DownloadSeatTokens = {
  section: string;
  row: string;
  seat: string;
};

export type DownloadSeatSource = {
  selectedSection?: string | null;
  selectedRow?: string | null;
  selectedSeat?: string | null;
  selectedSpecialSection?: string | null;
  selectedSectionIsSpecial?: boolean | null;
  selectedPlaceX?: number | null;
  selectedPlaceY?: number | null;
  selectedCustomerId?: string | null;
  selectedEmail?: string | null;
  /** Prefer StorageServiceInstance.isGaMomentContext() when available. */
  isGa?: boolean;
};

/** Strip characters that would break `section_row_seat` filename parsing. */
export function sanitizeDownloadSeatToken(value: string, fallback: string): string {
  const cleaned = String(value ?? "")
    .trim()
    .replace(/[^a-zA-Z0-9.-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return cleaned || fallback;
}

/**
 * Seat tokens used for operator Downloads / Single Image Per Seat.
 * GA: section = GA name, row = "GA", seat = rounded placeX/placeY (stable for View All).
 */
export function resolveDownloadSeatTokens(src: DownloadSeatSource): DownloadSeatTokens {
  const hasReservedSeat = Boolean(
    String(src.selectedSection || "").trim()
    || String(src.selectedRow || "").trim()
    || String(src.selectedSeat || "").trim(),
  );
  const isGa = src.isGa === true
    || src.selectedSectionIsSpecial === true
    || (
      !!String(src.selectedSpecialSection || "").trim()
      && !hasReservedSeat
    );

  if (isGa) {
    const gaName = String(src.selectedSpecialSection || src.selectedSection || "GA").trim();
    const section = sanitizeDownloadSeatToken(gaName, "GA");
    const row = "GA";
    let seatRaw = "";
    if (
      src.selectedPlaceX != null
      && src.selectedPlaceY != null
      && Number.isFinite(Number(src.selectedPlaceX))
      && Number.isFinite(Number(src.selectedPlaceY))
    ) {
      seatRaw = `${Math.round(Number(src.selectedPlaceX))}x${Math.round(Number(src.selectedPlaceY))}`;
    } else if (src.selectedCustomerId) {
      seatRaw = String(src.selectedCustomerId);
    } else if (src.selectedEmail) {
      seatRaw = String(src.selectedEmail).split("@")[0] || "fan";
    } else {
      seatRaw = "spot";
    }
    return {
      section,
      row,
      seat: sanitizeDownloadSeatToken(seatRaw, "spot"),
    };
  }

  return {
    section: sanitizeDownloadSeatToken(String(src.selectedSection || ""), "UnknownSection"),
    row: sanitizeDownloadSeatToken(String(src.selectedRow || ""), "UnknownRow"),
    seat: sanitizeDownloadSeatToken(String(src.selectedSeat || ""), "UnknownSeat"),
  };
}
